import { lazy } from 'react';

// Single source of truth for all app routes.
// App.jsx, Sidebar.jsx, Layout.jsx breadcrumbs, GlobalContextBar, and the
// mobile slide-out nav all consume this config. Adding a new page = one record.
//
// Fields:
//   id            Unique string key
//   path          React Router path (with :params for detail pages)
//   label         Display name in nav + breadcrumb
//   icon          Icon key consumed by Sidebar icon renderer
//   group         NAV_GROUPS id — controls sidebar section
//   component     React.lazy import (undefined = no component, redirect only)
//   breadcrumbs   string[] | (params, ctx) => string[] — static or dynamic
//   clientScoped  true → GlobalContextBar shows + URL carries ?client=&marketplace=
//   minRole       Minimum role to see in sidebar ('read_only'|'specialist'|'account_manager'|'admin')
//   navVisible    false → registered in router but hidden from nav (detail pages, admin routes)
//   desktopOnly   true → hidden on mobile nav (BetterOnDesktop — used in PR-09)
//   featureFlag   build-time env var name (must read 'true') gating nav visibility (e.g. 'VITE_SPAPI_UI_ENABLED')

export const ROUTES = [
  // ── Work ─────────────────────────────────────────────────────────────────
  {
    id: 'clients',
    path: '/clients',
    label: 'Clients',
    icon: 'clients',
    group: 'work',
    component: lazy(() => import('./pages/ClientsPage.jsx')),
    breadcrumbs: ['Clients'],
    clientScoped: false,
    minRole: 'read_only',
  },
  {
    id: 'client-detail',
    path: '/clients/:clientId',
    label: 'Client',
    icon: null,
    group: 'work',
    component: lazy(() => import('./pages/ClientDetailPage.jsx')),
    breadcrumbs: (params) => ['Clients', params.clientId],
    clientScoped: false,
    minRole: 'read_only',
    navVisible: false,
  },
  {
    id: 'growth',
    path: '/growth',
    label: 'Growth',
    icon: 'growth',
    group: 'work',
    component: lazy(() => import('./pages/GrowthPage.jsx')),
    breadcrumbs: ['Growth'],
    clientScoped: false,
    minRole: 'read_only',
  },

  // ── Catalogue ─────────────────────────────────────────────────────────────
  {
    id: 'catalogue',
    path: '/catalogue',
    label: 'Catalogue',
    icon: 'catalogue',
    group: 'catalogue',
    component: lazy(() => import('./pages/CataloguePage.jsx')),
    breadcrumbs: ['Catalogue'],
    clientScoped: true,
    minRole: 'read_only',
    desktopOnly: true,
  },
  {
    id: 'asin-detail',
    path: '/catalogue/:asin',
    label: 'ASIN',
    icon: null,
    group: 'catalogue',
    component: lazy(() => import('./pages/AsinDetailPage.jsx')),
    breadcrumbs: (params) => ['Catalogue', params.asin],
    clientScoped: true,
    minRole: 'read_only',
    navVisible: false,
  },
  {
    id: 'stock',
    path: '/stock',
    label: 'Stock',
    icon: 'stock',
    group: 'catalogue',
    component: lazy(() => import('./pages/StockPage.jsx')),
    breadcrumbs: ['Stock'],
    clientScoped: true,
    minRole: 'read_only',
    desktopOnly: true,
  },
  {
    id: 'copy-studio',
    path: '/copy-studio',
    label: 'Copy Studio',
    icon: 'copy',
    group: 'catalogue',
    component: lazy(() => import('./pages/CopyStudioPage.jsx')),
    breadcrumbs: ['Copy Studio'],
    clientScoped: true,
    minRole: 'specialist',
    desktopOnly: true,
  },
  {
    id: 'keywords',
    path: '/keywords',
    label: 'Keywords',
    icon: 'keywords',
    group: 'catalogue',
    component: lazy(() => import('./pages/KeywordsPage.jsx')),
    breadcrumbs: ['Keywords'],
    clientScoped: true,
    minRole: 'read_only',
  },

  // ── Performance ──────────────────────────────────────────────────────────
  {
    id: 'profit',
    path: '/profit',
    label: 'Profit',
    icon: 'profit',
    group: 'performance',
    component: lazy(() => import('./pages/ProfitabilityPage.jsx')),
    breadcrumbs: ['Profit'],
    clientScoped: true,
    minRole: 'read_only',
  },
  {
    id: 'forecast',
    path: '/forecast',
    label: 'Forecast',
    icon: 'forecast',
    group: 'performance',
    component: lazy(() => import('./pages/ForecastPage.jsx')),
    breadcrumbs: ['Forecast'],
    clientScoped: true,
    minRole: 'account_manager',
    desktopOnly: true,
  },
  {
    id: 'analytics',
    path: '/analytics',
    label: 'Analytics',
    icon: 'analytics',
    group: 'performance',
    component: lazy(() => import('./pages/AnalyticsPage.jsx')),
    breadcrumbs: ['Analytics'],
    clientScoped: true,
    minRole: 'read_only',
  },
  {
    id: 'alerts',
    path: '/alerts',
    label: 'Alerts',
    icon: 'alerts',
    group: 'performance',
    component: lazy(() => import('./pages/AlertsPage.jsx')),
    breadcrumbs: ['Alerts'],
    clientScoped: false,
    minRole: 'read_only',
  },
  {
    id: 'reports',
    path: '/reports',
    label: 'Reports',
    icon: 'reports',
    group: 'performance',
    component: lazy(() => import('./pages/ReportsPage.jsx')),
    breadcrumbs: ['Reports'],
    clientScoped: true,
    minRole: 'account_manager',
  },
  {
    id: 'report-edit',
    path: '/reports/:id/edit',
    label: 'Edit report',
    icon: null,
    group: 'performance',
    component: lazy(() => import('./pages/ReportEditPage.jsx')),
    breadcrumbs: ['Reports', 'Edit'],
    clientScoped: false,
    minRole: 'account_manager',
    navVisible: false,
  },

  // ── Market ────────────────────────────────────────────────────────────────
  {
    id: 'market-share',
    path: '/market-share',
    label: 'Market Share',
    icon: 'marketshare',
    group: 'market',
    component: lazy(() => import('./pages/MarketSharePage.jsx')),
    breadcrumbs: ['Market Share'],
    clientScoped: true,
    minRole: 'read_only',
  },
  {
    id: 'ppc-strategies',
    path: '/ppc-strategies',
    label: 'PPC Strategies',
    icon: 'ppc',
    group: 'market',
    component: lazy(() => import('./pages/PpcStrategiesPage.jsx')),
    breadcrumbs: ['PPC Strategies'],
    clientScoped: true,
    minRole: 'read_only',
  },
  {
    id: 'amazon-updates',
    path: '/amazon-updates',
    label: 'Amazon updates',
    icon: 'news',
    group: 'market',
    component: lazy(() => import('./pages/AmazonUpdatesPage.jsx')),
    breadcrumbs: ['Amazon updates'],
    clientScoped: false,
    minRole: 'read_only',
  },

  // ── Lab ───────────────────────────────────────────────────────────────────
  {
    id: 'intelligence',
    path: '/intelligence',
    label: 'Intelligence',
    icon: 'intelligence',
    group: 'lab',
    component: lazy(() => import('./pages/IntelligencePage.jsx')),
    breadcrumbs: ['Intelligence'],
    clientScoped: false,
    minRole: 'account_manager',
  },
  {
    id: 'market-sizing',
    path: '/market-sizing',
    label: 'Market Sizing',
    icon: 'marketsizing',
    group: 'lab',
    component: lazy(() => import('./pages/MarketSizingPage.jsx')),
    breadcrumbs: ['Market Sizing'],
    clientScoped: false,
    minRole: 'read_only',
  },
  {
    id: 'deal-tracker',
    path: '/deal-tracker',
    label: 'Deal Tracker',
    icon: 'dealtracker',
    group: 'lab',
    component: lazy(() => import('./pages/DealTrackerPage.jsx')),
    breadcrumbs: ['Deal Tracker'],
    clientScoped: false,
    minRole: 'read_only',
  },

  // ── System ────────────────────────────────────────────────────────────────
  {
    id: 'admin-agents',
    path: '/admin/agents',
    label: 'Agent runs',
    icon: 'alerts',
    group: 'system',
    component: lazy(() => import('./pages/AdminAgentsPage.jsx')),
    breadcrumbs: ['Agent runs'],
    clientScoped: false,
    minRole: 'admin',
  },
  {
    id: 'spapi-queue',
    path: '/admin/spapi-queue',
    label: 'SP-API queue',
    icon: 'alerts',
    group: 'system',
    component: lazy(() => import('./pages/SpApiQueuePage.jsx')),
    breadcrumbs: ['SP-API queue'],
    clientScoped: false,
    minRole: 'admin',
    featureFlag: 'VITE_SPAPI_UI_ENABLED',
  },
  {
    id: 'dev-primitives',
    path: '/dev/primitives',
    label: 'Primitives',
    icon: null,
    group: 'system',
    component: lazy(() => import('./pages/DevPrimitivesPage.jsx')),
    breadcrumbs: ['Dev', 'Primitives'],
    clientScoped: false,
    minRole: 'admin',
    navVisible: false,
  },
];

export const NAV_GROUPS = [
  { id: 'work',        label: 'Work' },
  { id: 'catalogue',   label: 'Catalogue' },
  { id: 'performance', label: 'Performance' },
  { id: 'market',      label: 'Market' },
  { id: 'lab',         label: 'Lab', defaultCollapsed: true },
  { id: 'system',      label: 'System' },
];

// Roles in ascending privilege order (used for minRole comparisons)
export const ROLE_ORDER = ['read_only', 'specialist', 'account_manager', 'admin'];

export function hasMinRole(userRole, minRole) {
  return ROLE_ORDER.indexOf(userRole) >= ROLE_ORDER.indexOf(minRole);
}

// Returns true when the route's build-time feature flag is satisfied (or
// when no flag is set). Routes guarded by a flag stay registered in the
// router so deep-links resolve and the page can render its own disabled
// empty state, but Sidebar uses this to keep them out of the nav.
export function isRouteFeatureEnabled(route) {
  if (!route.featureFlag) return true;
  return import.meta.env[route.featureFlag] === 'true';
}
